import React from 'react';
import SurveyContainer, {
  NavButtonProps,
} from '../../../components/Survey/Container';
import Title from '../../../components/Survey/Title';
import { Trans, useTranslation } from 'react-i18next';
import ImageHeader from '../../../components/Survey/ImageHeader';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useSupplierSurveyRaben } from '../hooks';
import PageLoading from '../../../components/PageLoading';
import NotFound from '../../../views/components/NotFound';
import { Subtitle } from '../styles';
import assets from '../assets';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Typography,
} from '@mui/material';
import OrganizationalLogo from 'src/components/OrganizationalLogo';
import { toast } from 'react-toastify';
import LogoWithRabenRed from '../Intro/LogoWithRabenRed';
import TextField from 'src/components/TextField';
import RegistrationTarget from 'src/views/Pages/RegisterRaben/components/RegistrationTarget';

export default function SupplierSurveyRabenIntro() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams();
  const { data, isError, getSurveyToken } = useSupplierSurveyRaben();

  const [searchParams, _] = useSearchParams();
  const reportingPeriod =
    searchParams.get('reporting_period') ??
    String(new Date().getFullYear() - 1);

  const [vatIdentifier, setVatIdentifier] = React.useState<string>(
    searchParams.get('vat_identifier') ?? ''
  );

  const [error, setError] = React.useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);

  // If the URL token is an access token (JWT) rather than a registration UUID,
  // redirect into the access-token flow instead of calling survey-raben endpoints.
  const isUuid =
    typeof token === 'string' &&
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
      token
    );
  if (token && !isUuid) {
    return <Navigate to={`main-page${location.search}`} replace />;
  }

  if (isError) return <NotFound />;
  if (!data) return <PageLoading />;

  const onNext = (acceptMatch: boolean) => {
    getSurveyToken.mutate(
      { vatIdentifier, acceptMatch, reportingPeriod },
      {
        onSuccess: (data) => {
          navigate(
            `/suppliers/${data.token}/initial-questions?vat_identifier=${vatIdentifier}&registration_token=${token}`
          );
        },
        onError: (error: any) => {
          console.log(error);
          if (error?.response?.status === 402) {
            toast.info(t('ghg.supplierSurvey.intro.matchInfo') as string);
            setDialogOpen(true);
          } else {
            toast.error(
              t('ghg.supplierSurvey.intro.vatIdentifierError') as string
            );
            setError(t('ghg.supplierSurvey.intro.vatIdentifierError'));
          }
        },
      }
    );
  };

  const nextButton: NavButtonProps = {
    label: 'ghg.supplierSurvey.next',
    onClick: () => onNext(false),
  };

  return (
    <SurveyContainer
      logo={<LogoWithRabenRed />}
      nextLink={nextButton}
      header={
        <ImageHeader
          imageSrc={assets.intro.image}
          text={t('ghg.supplierSurvey.welcomeHeader')}
        />
      }
    >
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>{t('ghg.supplierSurvey.intro.dialogTitle')}</DialogTitle>
        <DialogActions>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            width="100%"
          >
            <Button onClick={() => setDialogOpen(false)} variant="secondary">
              {t('ghg.supplierSurvey.intro.cancelButton')}
            </Button>
            <Button onClick={() => onNext(true)}>
              {t('ghg.supplierSurvey.intro.confirmButton')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <div>
        <Title title={t('ghg.supplierSurvey.intro.title')} />
        <Subtitle>{t('ghg.supplierSurvey.intro.subtitle')}</Subtitle>
        <Trans i18nKey="ghg.supplierSurvey.intro.message" />
        <Typography>{data.organization_name}</Typography>
        <OrganizationalLogo logo={data.organization_logo} />
        <RegistrationTarget target={data} sx={{ mt: '32px' }} />
        <Typography variant="subtitle2" sx={{ mt: '48px' }}>
          {t('ghg.supplierSurvey.intro.vatIdentifierSubtitle')}
        </Typography>
        <TextField
          required
          label={t('ghg.supplierSurvey.intro.vatIdentifier')}
          value={vatIdentifier}
          onChange={(e) => setVatIdentifier(e.target.value)}
          fullWidth
          sx={{ mt: '24px' }}
          errorText={error}
        />
      </div>
    </SurveyContainer>
  );
}
